/**
 * KAN-727: data the list upload form is set up from.
 *
 * - presets: last remembered uploads of this list kind (latest first);
 * - headers: column headers + sample rows of the chosen file, or of the
 *   Google Sheet link (debounced, only a valid docs.google.com link). A file
 *   wins over a link, as in the upload itself.
 */
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '@/hooks/useDebounce';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { isGoogleSheetUrl } from '@/lib/uploadSummary';
import { datasetsApi } from '@/services/api/datasets';
import type { CampaignKind, UploadHeaders, UploadPreset } from '@/types/dataset';

export const SHEET_URL_DEBOUNCE_MS = 600;

export function useUploadPresets(kind: CampaignKind | undefined) {
  return useQuery<UploadPreset[]>({
    queryKey: ['datasetUploadPresets', kind],
    queryFn: () => datasetsApi.uploadPresets(kind),
    enabled: !!kind,
    retry: false,
  });
}

export interface UploadHeadersState {
  data: UploadHeaders | undefined;
  error: string | null;
  isLoading: boolean;
}

const fileKey = (file: File) => `file:${file.name}:${file.size}:${file.lastModified}`;

export function useUploadHeaders(
  datasetId: string,
  file: File | null,
  sheetUrl: string
): UploadHeadersState {
  const debouncedUrl = useDebounce(sheetUrl.trim(), SHEET_URL_DEBOUNCE_MS);
  const useSheet = !file && isGoogleSheetUrl(debouncedUrl);
  const sourceKey = file ? fileKey(file) : useSheet ? `sheet:${debouncedUrl}` : null;

  const query = useQuery<UploadHeaders>({
    queryKey: ['datasetUploadHeaders', datasetId, sourceKey],
    queryFn: () =>
      datasetsApi.uploadHeaders(datasetId, file ? { file } : { sheetUrl: debouncedUrl }),
    enabled: !!datasetId && !!sourceKey,
    retry: false,
    staleTime: Infinity,
  });

  if (!sourceKey) {
    return { data: undefined, error: null, isLoading: false };
  }
  return {
    data: query.data,
    error: query.error
      ? extractErrorMessage(query.error, 'Не удалось прочитать заголовки колонок')
      : null,
    isLoading: query.isFetching,
  };
}
